import React, { useCallback, useRef } from 'react';
import { Trip, ApartmentType } from '@/types';
import { updateTrip } from '@/services/tripService';
import { Button } from '@mui/material';
import { Add } from '@mui/icons-material';
import TripDashboardSection from '../TripDashboardSection';
import ApartmentTypesPage from '@/components/apartmentType/ApartmentTypesPage';
import AddApartmentTypeForm from '@/components/apartmentType/AddApartmentTypeForm';
import { useI18n } from '@/hooks/useI18n';

interface TripApartmentTypesPageProps {
  trip: Trip;
}

const TripApartmentTypesPage: React.FC<TripApartmentTypesPageProps> = ({ trip }) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { t } = useI18n();

  const updateTripCallback = useCallback(async (updatedTripData: Partial<Trip>) => {
    await updateTrip({ ...trip, ...updatedTripData });
  }, [trip]);

  const addApartmentType = useCallback((newApartmentType: Omit<ApartmentType, 'id'>) => {
    const id = `apartment-type-${Date.now()}`;
    updateTripCallback({ apartmentTypes: [...trip.apartmentTypes, { ...newApartmentType, id }] });
  }, [trip, updateTripCallback]);

  const updateApartmentType = useCallback((updatedApartmentType: ApartmentType) => {
    updateTripCallback({
      apartmentTypes: trip.apartmentTypes.map(s =>
        s.id === updatedApartmentType.id ? updatedApartmentType : s
      )
    });
  }, [trip, updateTripCallback]);

  const deleteApartmentType = useCallback((apartmentTypeId: string) => {
    updateTripCallback({
      apartmentTypes: trip.apartmentTypes.filter(s => s.id !== apartmentTypeId)
    });
  }, [trip, updateTripCallback]);

  return (
    <>
      <TripDashboardSection
        icon="🏡"
        title={t('trip.pages.apartmentTypes.title')}
        subtitle={t('trip.pages.apartmentTypes.subtitle')}
        description={t('trip.pages.apartmentTypes.description')}
        primaryAction={{
          icon: <Add />,
          onClick: () => {
            triggerRef.current?.click();
          },
          label: t('trip.pages.apartmentTypes.addApartmentType')
        }}
      >
        <ApartmentTypesPage 
          apartmentTypes={trip.apartmentTypes} 
          onUpdateApartmentType={updateApartmentType} 
          onAddApartmentType={addApartmentType} 
          onDeleteApartmentType={deleteApartmentType} 
        />
      </TripDashboardSection>

      <AddApartmentTypeForm
        onAddApartmentType={addApartmentType}
        trigger={
          <Button 
            ref={triggerRef}
            style={{ display: 'none' }}
          >
            {t('trip.pages.apartmentTypes.hidden')}
          </Button>
        }
      />
    </>
  );
};

export default TripApartmentTypesPage;
